import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, ContentChild, ElementRef, input, output, ViewChild } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { ModalConfig, ModalConfigAction } from "../modal";
import { SvgComponent } from "../svg";

@Component({
    selector: "m-modal",
    standalone: true,
    imports: [CommonModule, SvgComponent, TranslateModule],
    templateUrl: "./modal.component.html",
    styleUrl: "./modal.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalComponent {
    @ViewChild("modal", { static: true }) modalElement!: ElementRef<HTMLDialogElement>;
    @ViewChild("modalContent", { static: true }) modalContentElement!: ElementRef;

    config?: ModalConfig;
    doNotShowAgain = false;
    hasFooter = false;
    submitting = false;

    close = output<ModalConfigAction>();

    @ContentChild("footer") set setFooter(footer: HTMLElement) {
        this.hasFooter = !!footer;
    }

    title = input.required<string>();
    canClose = input<boolean>(true);
    style = input<any>();

    get modalOpen() {
        return this.modalElement.nativeElement.open;
    }

    openModal(event?: Event) {
        if (event) event.stopPropagation();
        this.modalElement.nativeElement.show();
        this.resetModalPosition();

        window.addEventListener("keydown", this.keyPressHandler, true);

        if (this.canClose()) {
            window.addEventListener("click", this.mouseClickHandler, true);
        }
    }

    closeModal(action?: ModalConfigAction) {
        this.submitting = true;
        this.close.emit(action);

        if (this.canClose()) {
            window.removeEventListener("click", this.mouseClickHandler, true);
        }

        window.removeEventListener("keydown", this.keyPressHandler, true);

        this.modalElement.nativeElement.close();
        this.submitting = false;
    }

    keyPressHandler = (event: KeyboardEvent) => {
        if (event.key !== "Escape") return;
        if (this.canClose()) {
            this.closeModal(this.config?.outsideClickAction);
        }
    };

    mouseClickHandler = (event: Event) => {
        const userClickedOutside = this.isOutsideOfElement(event, this.modalContentElement.nativeElement);
        if (userClickedOutside && this.config?.canClose === true) {
            this.closeModal(this.config?.outsideClickAction);
        }
    };

    isOutsideOfElement(event: Event, element: any) {
        return !element.contains(event.target);
    }

    onChangeDoNotShow(event: Event) {
        this.doNotShowAgain = (event.target as HTMLInputElement).checked;
    }

    //region Dragging
    isDragging = false;
    initialX = 0;
    initialY = 0;
    offsetX = 0;
    offsetY = 0;

    protected startDragging(event: MouseEvent) {
        event.preventDefault();

        this.initialX = event.clientX;
        this.initialY = event.clientY;

        const style = window.getComputedStyle(this.modalContentElement.nativeElement);
        this.offsetX = parseInt(style.getPropertyValue("--modal-content-offset-x") || "0", 10);
        this.offsetY = parseInt(style.getPropertyValue("--modal-content-offset-y") || "0", 10);

        this.isDragging = true;

        document.addEventListener("mousemove", this.onDrag);
        document.addEventListener("mouseup", this.stopDrag);
    }

    onDrag = (event: MouseEvent) => {
        if (!this.isDragging) return;

        const dx = event.clientX - this.initialX;
        const dy = event.clientY - this.initialY;

        const newOffsetX = this.offsetX + dx;
        const newOffsetY = this.offsetY + dy;

        this.updatePosition(newOffsetX, newOffsetY);
    };

    stopDrag = () => {
        this.isDragging = false;

        document.removeEventListener("mousemove", this.onDrag);
        document.removeEventListener("mouseup", this.stopDrag);
    };

    /**
     * Update the position of the modal while dragging but make sure the modal always stays inside the viewport
     * @param offsetX
     * @param offsetY
     * @private
     */
    private updatePosition(offsetX: number, offsetY: number) {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const modalContent = this.modalContentElement.nativeElement;
        const modalWidth = modalContent.offsetWidth;
        const modalHeight = modalContent.offsetHeight;

        const centerX = (viewportWidth - modalWidth) / 2;
        const centerY = (viewportHeight - modalHeight) / 2;

        const maxLeftOffset = centerX; // Distance from center to left edge
        const maxRightOffset = viewportWidth - centerX - modalWidth; // Distance from center to right edge
        const maxTopOffset = centerY; // Distance from center to top edge
        const maxBottomOffset = viewportHeight - centerY - modalHeight; // Distance from center to bottom edge

        const finalX = Math.max(-maxLeftOffset, Math.min(maxRightOffset, offsetX));
        const finalY = Math.max(-maxTopOffset, Math.min(maxBottomOffset, offsetY));

        this.modalContentElement.nativeElement.style.setProperty("--modal-content-offset-x", `${finalX}px`);
        this.modalContentElement.nativeElement.style.setProperty("--modal-content-offset-y", `${finalY}px`);
    }

    private resetModalPosition() {
        this.modalContentElement.nativeElement.style.setProperty("--modal-content-offset-x", "0px");
        this.modalContentElement.nativeElement.style.setProperty("--modal-content-offset-y", "0px");
    }

    //endregion
}
